import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  ArrowRight, 
  ArrowLeft,
  Briefcase,
  Lightbulb,
  Search,
  Sparkles,
  Target,
  Plus,
  X
} from 'lucide-react';

/**
 * HP.3 Guided Input: Capability Insight Wizard
 * Captures user projects and skills to generate a capability map.
 * 3 steps: Role → Projects → Self-Assessment
 */

const STEPS = [
  {
    id: 'role',
    title: 'About You',
    subtitle: 'Quick context about your role',
    icon: Briefcase
  },
  {
    id: 'projects',
    title: 'Recent Work',
    subtitle: 'Projects and outcomes you can point to',
    icon: Target
  },
  {
    id: 'assessment',
    title: 'Self-Assessment',
    subtitle: 'What you think — and what you\'re unsure about',
    icon: Search
  }
];

const InsightGuidedInput = ({ onComplete, onCancel, accentColor = '#f59e0b' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    role: '',
    context: '',
    projects: [
      { name: '', outcome: '' },
      { name: '', outcome: '' },
    ],
    known_strengths: '',
    uncertainties: '',
    optional_paste: ''
  });
  const [showOptionalPaste, setShowOptionalPaste] = useState(false);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateProject = (index, field, value) => {
    setFormData(prev => {
      const newProjects = [...prev.projects];
      newProjects[index] = { ...newProjects[index], [field]: value };
      return { ...prev, projects: newProjects };
    });
  };

  const addProject = () => {
    if (formData.projects.length < 5) {
      setFormData(prev => ({
        ...prev,
        projects: [...prev.projects, { name: '', outcome: '' }]
      }));
    }
  };

  const removeProject = (index) => {
    if (formData.projects.length > 1) {
      setFormData(prev => ({
        ...prev,
        projects: prev.projects.filter((_, i) => i !== index)
      }));
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.role.trim().length >= 2 && formData.context.trim().length >= 10;
      case 1:
        return formData.projects.some(p => p.name.trim().length > 0);
      case 2:
        return true; // Self-assessment is optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const filledProjects = formData.projects
        .filter(p => p.name.trim())
        .map(p => `${p.name}${p.outcome ? ` — Outcome: ${p.outcome}` : ''}`);

      onComplete({
        role: formData.role.trim(),
        context: formData.context.trim(),
        achievements: filledProjects,
        challenges: formData.uncertainties.trim(),
        growth: formData.known_strengths.trim(),
        goals: '',
        optional_paste: formData.optional_paste.trim(),
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const StepIcon = STEPS[currentStep].icon;

  return (
    <div data-testid="insight-guided-input" className="max-w-2xl mx-auto">
      {/* Progress Bar */}
      <div data-testid="insight-progress-bar" className="mb-8">
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Step {currentStep + 1} of {STEPS.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, backgroundColor: accentColor }}
          />
        </div>
      </div>

      {/* Step Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
          <StepIcon className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">{STEPS[currentStep].title}</h3>
          <p className="text-sm text-gray-400">{STEPS[currentStep].subtitle}</p>
        </div>
      </div>

      {/* Step Content */}
      <div className="space-y-4">
        {currentStep === 0 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Your role</label>
              <input
                data-testid="insight-role-input"
                type="text"
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value)}
                placeholder="e.g. Senior Developer, Product Manager, IT Manager"
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': accentColor }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">What does your day-to-day look like?</label>
              <textarea
                data-testid="insight-context-input"
                value={formData.context}
                onChange={(e) => updateField('context', e.target.value)}
                placeholder="Describe your typical responsibilities, who you work with, what kind of problems you solve..."
                rows={4}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
              <p className="text-xs text-gray-500 mt-1">Min 10 characters — the more detail, the better the analysis</p>
            </div>
          </>
        )}

        {currentStep === 1 && (
          <>
            <p className="text-sm text-gray-400 mb-2">
              Describe projects, tasks, or initiatives you've worked on recently. What did you do? What happened?
            </p>
            {formData.projects.map((project, i) => (
              <div key={i} className="p-4 bg-gray-800/30 border border-gray-700/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Project {i + 1}</span>
                  {formData.projects.length > 1 && (
                    <button onClick={() => removeProject(i)} className="text-gray-500 hover:text-red-400 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  data-testid={`insight-project-name-${i}`}
                  type="text"
                  value={project.name}
                  onChange={(e) => updateProject(i, 'name', e.target.value)}
                  placeholder="What did you work on?"
                  className="w-full px-3 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                  style={{ '--tw-ring-color': accentColor }}
                />
                <input
                  data-testid={`insight-project-outcome-${i}`}
                  type="text"
                  value={project.outcome}
                  onChange={(e) => updateProject(i, 'outcome', e.target.value)}
                  placeholder="What was the outcome? (optional)"
                  className="w-full px-3 py-2.5 bg-gray-900/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                  style={{ '--tw-ring-color': accentColor }}
                />
              </div>
            ))}
            {formData.projects.length < 5 && (
              <button
                data-testid="insight-add-project"
                onClick={addProject}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                <Plus className="w-4 h-4" /> Add another project
              </button>
            )}
          </>
        )}

        {currentStep === 2 && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                What do you think you're good at?
              </label>
              <textarea
                data-testid="insight-strengths-input"
                value={formData.known_strengths}
                onChange={(e) => updateField('known_strengths', e.target.value)}
                placeholder="e.g. Problem solving under pressure, mentoring junior staff, translating technical concepts for non-technical people..."
                rows={3}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
              <p className="text-xs text-gray-500 mt-1">Optional — but helps us compare your self-view with the evidence</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                What are you unsure about?
              </label>
              <textarea
                data-testid="insight-uncertainties-input"
                value={formData.uncertainties}
                onChange={(e) => updateField('uncertainties', e.target.value)}
                placeholder="e.g. Am I actually strategic or just reactive? Do I add real value in meetings? Is my technical knowledge still current enough?"
                rows={3}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                style={{ '--tw-ring-color': accentColor }}
              />
              <p className="text-xs text-gray-500 mt-1">Optional — these doubts help the AI look for evidence you might be missing</p>
            </div>

            {/* Optional paste */}
            <div className="pt-2">
              {!showOptionalPaste ? (
                <button
                  onClick={() => setShowOptionalPaste(true)}
                  className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Have feedback or a review to paste? (optional)
                </button>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Paste any feedback, review, or peer comments (optional)
                  </label>
                  <textarea
                    data-testid="insight-paste-input"
                    value={formData.optional_paste}
                    onChange={(e) => updateField('optional_paste', e.target.value)}
                    placeholder="Paste performance review excerpts, peer feedback, manager comments, 360 feedback..."
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                    style={{ '--tw-ring-color': accentColor }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-4 border-t border-gray-800">
        <div>
          {currentStep > 0 ? (
            <Button
              data-testid="insight-back-btn"
              variant="ghost"
              onClick={handleBack}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
          ) : (
            <Button
              data-testid="insight-cancel-btn"
              variant="ghost"
              onClick={onCancel}
              className="text-gray-400 hover:text-white"
            >
              Cancel
            </Button>
          )}
        </div>
        <Button
          data-testid="insight-next-btn"
          onClick={handleNext}
          disabled={!canProceed()}
          className="text-white font-medium px-6"
          style={{ backgroundColor: canProceed() ? accentColor : undefined }}
        >
          {currentStep < STEPS.length - 1 ? (
            <>Next <ArrowRight className="w-4 h-4 ml-1.5" /></>
          ) : (
            <>Analyse My Capabilities <Sparkles className="w-4 h-4 ml-1.5" /></>
          )}
        </Button>
      </div>
    </div>
  );
};

export default InsightGuidedInput;
